// The admin Program screen's calls to the backend, all of them under /api/cms/** (the admin area: admins and the team, authority.StaffRoutes).
// Every function answers the response body; the axios helper is the one the other admin screens use
// (it attaches the admin JWT and refreshes it). The route-shape comments name the controller each
// call hits; the shapes are the records of that controller's view/service class.
//
// Kept in one module so the screen's container can be tested against a mocked API (the tests mock
// this module and assert on the payloads, not on axios).
import api from '../../utils/authUtils';
import { matchParams } from '../personAdmin/PersonAdminApi';

const params = (extra) => ({ params: extra });

// ---------------------------------------------------------------- loading the screen

// program.EventOptionsController
export const fetchEventOptions = () => api.get('/api/cms/events/options').then((r) => r.data);

// program.ProgramController (ProgramView); removed nodes only when asked for
export const fetchProgram = (eventId, includeRemoved = true) => api
  .get(`/api/cms/events/${eventId}/program`, params({ includeRemoved })).then((r) => r.data);

// invitation.EventPeopleController (EventPeopleService.Row[])
export const fetchPeople = (eventId) => api.get(`/api/cms/events/${eventId}/people`).then((r) => r.data);

// person.PersonAdminController search: the "pick a person" box
export const searchPeople = (q, limit = 20) => api
  .get('/api/cms/people/search', params({ q: q || '', limit })).then((r) => r.data);

// person.PersonAdminController matches and create: "create a new person" from the picker, with the
// same duplicate check as the People screen (PersonAdminApi has the same two calls).
export const findPersonMatches = ({ firstName, lastName, email } = {}) => api
  .get('/api/cms/people/matches', params(matchParams({ firstName, lastName, email }))).then((r) => r.data);

export const createPerson = (fields) => api.post('/api/cms/people', fields).then((r) => r.data);

// person.PersonAdminController edit, only for the contact email an invitation needs (refused with
// 409 for an account holder - who always has one - and for an address another live person has).
export const savePersonContactEmail = (personId, contactEmail) => api
  .put(`/api/cms/people/${personId}`, { contactEmail }).then((r) => r.data);

// publicapi.ProgramPreviewController (the public shape of the draft program)
export const fetchPublicPreview = (eventId) => api
  .get(`/api/cms/events/${eventId}/program/public-preview`).then((r) => r.data);

export const fetchPublicPreviewPeople = (eventId) => api
  .get(`/api/cms/events/${eventId}/program/public-preview/people`).then((r) => r.data);

// ---------------------------------------------------------------- program settings

export const publishProgram = (eventId, pdfPublic) => api
  .post(`/api/cms/events/${eventId}/program/publish`, { pdfPublic }).then((r) => r.data);

export const unpublishProgram = (eventId) => api
  .post(`/api/cms/events/${eventId}/program/unpublish`).then((r) => r.data);

// ---------------------------------------------------------------- entries and spots

// AssignmentController.add (AssignmentRequests.Add)
export const addAssignment = (eventId, body) => api
  .post(`/api/cms/events/${eventId}/program/assignments`, body).then((r) => r.data);

// AssignmentController.update: a PUT, so the labels and the note go along unchanged
export const updateAssignment = (eventId, id, body) => api
  .put(`/api/cms/events/${eventId}/program/assignments/${id}`, body).then((r) => r.data);

// AssignmentController.move ("to another entry of the event (never asked only)")
export const moveAssignment = (eventId, id, body) => api
  .put(`/api/cms/events/${eventId}/program/assignments/${id}/move`, body).then((r) => r.data);

// AssignmentController.replacePerson; `notify` tells the person whose open invitation it cancels
export const replaceAssignmentPerson = (eventId, id, body) => api
  .put(`/api/cms/events/${eventId}/program/assignments/${id}/person`, body).then((r) => r.data);

// AssignmentController.remove: deleted when nobody was asked, else removed; `notify` tells the people
// whose open invitations it cancels
export const removeAssignment = (eventId, id, notify) => api
  .delete(`/api/cms/events/${eventId}/program/assignments/${id}`, { data: { notify } }).then((r) => r.data);

export const declineAssignment = (eventId, id, body) => api
  .post(`/api/cms/events/${eventId}/program/assignments/${id}/decline`, body).then((r) => r.data);

export const withdrawAssignment = (eventId, id, notify) => api
  .post(`/api/cms/events/${eventId}/program/assignments/${id}/withdraw`, { notify }).then((r) => r.data);

export const uninviteAssignment = (eventId, id, notify) => api
  .post(`/api/cms/events/${eventId}/program/assignments/${id}/uninvite`, { notify }).then((r) => r.data);

// AssignmentController.removeEntry (ProgramController DELETE .../entries/{id})
export const removeEntry = (eventId, id, notify) => api
  .delete(`/api/cms/events/${eventId}/program/entries/${id}`, { data: { notify } }).then((r) => r.data);

// ProgramChangeController POST .../changes[/preview] (ProgramRequests.EntryChange): one change to an
// entry - {entry, roomId} or {move} - plus the assignments whose people get the information email,
// and the admin's optional one-time `template`. The preview answers {affected, emails,
// templateProblems, template, placeholders}; the change answers {entry, affected, notices}. The
// Edit dialog and every move go through these, so the admin chooses who is told.
export const previewEntryChange = (eventId, entryId, body) => api
  .post(`/api/cms/events/${eventId}/program/entries/${entryId}/changes/preview`, body).then((r) => r.data);

export const changeEntry = (eventId, entryId, body) => api
  .post(`/api/cms/events/${eventId}/program/entries/${entryId}/changes`, body).then((r) => r.data);

// ProgramController.createEntry (ProgramRequests.CreateEntry)
export const createEntry = (eventId, body) => api
  .post(`/api/cms/events/${eventId}/program/entries`, body).then((r) => r.data);

// ---------------------------------------------------------------- notices (preview + send)

// AssignmentController.previewCancellation: default subject/body per person; empty unless INVITED;
// with action=withdraw the withdrawal notice instead (empty unless CONFIRMED)
export const previewAssignmentNotices = (eventId, id, action) => api
  .post(`/api/cms/events/${eventId}/program/assignments/${id}/cancellation-notices/preview`,
    null, action ? params({ action }) : undefined).then((r) => r.data);

// ProgramController.previewEntryRemovalNotices
export const previewEntryNotices = (eventId, entryId) => api
  .post(`/api/cms/events/${eventId}/program/entries/${entryId}/cancellation-notices/preview`).then((r) => r.data);

// ---------------------------------------------------------------- invitations

// invitation.InvitationController POST /preview (a dummy link; nothing saved) and POST (one INVITE
// email per call, one person + their assignmentIds). Both take an optional `template` (the admin's
// edited subject/body, rendered per person, never saved); omitting it keeps the built-in invitation.
// The preview answer (RenderedMail) also carries `template {subject, html}` - the editable
// {{placeholder}} text the email was rendered from - and `placeholders`, the list it may use.
export const previewInvitation = (eventId, body) => api
  .post(`/api/cms/events/${eventId}/invitations/preview`, body).then((r) => r.data);

export const sendInvitation = (eventId, body) => api
  .post(`/api/cms/events/${eventId}/invitations`, body).then((r) => r.data);

// invitation.SuggestedPersonController
export const fetchSuggestedPerson = (eventId, assignmentId) => api
  .get(`/api/cms/events/${eventId}/program/assignments/${assignmentId}/suggested-person`).then((r) => r.data);

export const applySuggestedPerson = (eventId, assignmentId, seen) => api
  .post(`/api/cms/events/${eventId}/program/assignments/${assignmentId}/suggested-person`, seen).then((r) => r.data);

// ---------------------------------------------------------------- reminders

// ReminderController preview {personId, subject?, html?, highlight?} / send {personIds[], template?} /
// stop|resume {personId}. The reminder is one global template rendered per person, so the admin's
// edited subject and body go back as one `template` for the whole send (never saved); omitting it
// keeps the saved invite-reminder template. The preview answer (RenderedMail) also carries
// `template {subject, html}` - the saved template or the edit - and `placeholders`, the list it may use.

// The list: one row per person with an invitation at the event ({personId, personName,
// openInvitations, stoppedInvitations, lastSentAt, nextAutomatic, due, linkExpiresAt}). The reminder
// template editor reads it for the people a preview can be rendered for.
export const fetchReminders = (eventId) => api.get(`/api/cms/events/${eventId}/reminders`).then((r) => r.data);

export const previewReminder = (eventId, body) => api
  .post(`/api/cms/events/${eventId}/reminders/preview`, body).then((r) => r.data);

export const sendReminders = (eventId, personIds, template) => api
  .post(`/api/cms/events/${eventId}/reminders/send`, template ? { personIds, template } : { personIds })
  .then((r) => r.data);

export const stopReminders = (eventId, personId) => api
  .post(`/api/cms/events/${eventId}/reminders/stop`, { personId }).then((r) => r.data);

export const resumeReminders = (eventId, personId) => api
  .post(`/api/cms/events/${eventId}/reminders/resume`, { personId }).then((r) => r.data);

// The reminder template is edited on the E-Mails screen since P2b (emailAdmin/EmailAdminApi); this
// module only previews and sends with the saved one.

// ---------------------------------------------------------------- imports & program PDF

// programimport.ProgramImportController
export const fetchImports = (eventId) => api.get(`/api/cms/events/${eventId}/program/imports`).then((r) => r.data);

export const uploadImport = (eventId, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/api/cms/events/${eventId}/program/imports`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};

export const extractImport = (eventId, id) => api
  .post(`/api/cms/events/${eventId}/program/imports/${id}/extract`).then((r) => r.data);

export const previewImport = (eventId, id, decisions) => api
  .post(`/api/cms/events/${eventId}/program/imports/${id}/preview`, decisions || {}).then((r) => r.data);

export const applyImport = (eventId, id, decisions) => api
  .post(`/api/cms/events/${eventId}/program/imports/${id}/apply`, decisions || {}).then((r) => r.data);

export const fetchProgramPdf = (eventId) => api
  .get(`/api/cms/events/${eventId}/program/imports/program-pdf`).then((r) => r.data);

export const chooseProgramPdf = (eventId, importId) => api
  .put(`/api/cms/events/${eventId}/program/imports/program-pdf`, { importId }).then((r) => r.data);

// programimport.GeneratedProgramPdf: whether the server can print the program, and doing it (the
// server's Chromium needs some seconds, hence the long timeout).
export const fetchGeneratedPdfAvailable = (eventId) => api
  .get(`/api/cms/events/${eventId}/program/imports/generated-pdf`).then((r) => !!r.data?.available);

export const generateProgramPdf = (eventId) => api
  .post(`/api/cms/events/${eventId}/program/imports/generated-pdf`, null, { timeout: 240000 }).then((r) => r.data);

// programimport.ProgramPdfPages: the program's own pages - title pages (part "cover", in front of the
// PDF and in place of the event page's main image) and sponsor pages ("sponsors", where
// setSponsorsPosition puts them on the website and in the PDF).
export const fetchPdfPages = (eventId) => api
  .get(`/api/cms/events/${eventId}/program/imports/pdf-pages`).then((r) => r.data);

export const addPdfPage = (eventId, part, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/api/cms/events/${eventId}/program/imports/pdf-pages/${part}`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};

export const addEventImagePage = (eventId) => api
  .post(`/api/cms/events/${eventId}/program/imports/pdf-pages/cover/from-event-image`).then((r) => r.data);

export const movePdfPage = (eventId, pageId, up) => api
  .post(`/api/cms/events/${eventId}/program/imports/pdf-pages/${pageId}/move`, null, { params: { up } }).then((r) => r.data);

export const removePdfPage = (eventId, pageId) => api
  .delete(`/api/cms/events/${eventId}/program/imports/pdf-pages/${pageId}`).then((r) => r.data);

// Whether a page file is shown on the website and in the PDF ({ website, pdf }; one left out stays).
export const setPdfPageShown = (eventId, pageId, shown) => api
  .put(`/api/cms/events/${eventId}/program/imports/pdf-pages/${pageId}/shown`, shown).then((r) => r.data);

// position: BEFORE_PEOPLE, AFTER_PEOPLE or AFTER_ENTRY (then afterEntryId, a top-level entry).
export const setSponsorsPosition = (eventId, position, afterEntryId) => api
  .put(`/api/cms/events/${eventId}/program/imports/pdf-pages/sponsors-position`,
    { position, afterEntryId: position === 'AFTER_ENTRY' ? afterEntryId : null })
  .then((r) => r.data);

// One page as an image (index: the page of a PDF), a Blob like the file below.
export const fetchPdfPageImage = (eventId, pageId, index) => api
  .get(`/api/cms/events/${eventId}/program/imports/pdf-pages/${pageId}/image/${index}`, { responseType: 'blob' })
  .then((r) => r.data);

// A page's file (a Blob: the admin route needs the login header, so no plain <img src>).
export const fetchPdfPageFile = (eventId, pageId) => api
  .get(`/api/cms/events/${eventId}/program/imports/pdf-pages/${pageId}/file`, { responseType: 'blob' }).then((r) => r.data);

// The PDF as the server would make it now, in one language, also for a draft (a Blob, nothing kept).
export const fetchProgramPdfPreview = (eventId, lang) => api
  .get(`/api/cms/events/${eventId}/program/imports/generated-pdf/preview`,
    { params: { lang }, responseType: 'blob', timeout: 180000 })
  .then((r) => r.data);

// ---------------------------------------------------------------- organizers & committee

// eventbody.EventBodyController / OrganizationController
export const fetchBodies = (eventId) => api.get(`/api/cms/events/${eventId}/bodies`).then((r) => r.data);

export const addBodyMember = (eventId, body) => api
  .post(`/api/cms/events/${eventId}/bodies`, body).then((r) => r.data);

export const removeBodyMember = (eventId, id) => api
  .delete(`/api/cms/events/${eventId}/bodies/${id}`).then((r) => r.data);

export const fetchOrganizations = () => api.get('/api/cms/organizations').then((r) => r.data);

export const createOrganization = (body) => api.post('/api/cms/organizations', body).then((r) => r.data);

export const deleteOrganization = (id) => api.delete(`/api/cms/organizations/${id}`).then((r) => r.data);

// ---------------------------------------------------------------- talk files (staff)

// material.MaterialController (MaterialViews): every talk of the event - one row per entry that asks
// for slides (or waits for a title suggestion) - with its speakers, files in order, versions,
// presenters, what blocks publication and the order's state.
export const fetchMaterials = (eventId) => api.get(`/api/cms/events/${eventId}/materials`).then((r) => r.data);

// A file is behind the bearer token, so it cannot be a plain <a href>: fetch it and hand the blob to
// the browser as a download (the same trick as selfApi.downloadDeck).
// The backend names a staff download with the deck's position ("01_<name>") and the zip
// "materials-<entryId>.zip"; use its Content-Disposition when the response carries it, so the
// browser saves exactly that name, and fall back to the caller's name.
const fileNameOf = (headers, fallback) => {
  const disposition = headers?.['content-disposition'] || headers?.get?.('content-disposition');
  if (!disposition) return fallback;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) return decodeURIComponent(encoded[1]);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : fallback;
};

const downloadBlob = (data, fileName, headers) => {
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileNameOf(headers, fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// `versionId` is a version id; the backend names the file with the deck's position.
export const downloadMaterial = async (eventId, versionId, fileName) => {
  const response = await api.get(`/api/cms/events/${eventId}/materials/${versionId}/file`, { responseType: 'blob' });
  downloadBlob(response.data, fileName || 'slides.pptx', response.headers);
};

// The whole talk as one zip: the current version of every file, named "01_<name>", "02_...".
export const downloadDecksZip = async (eventId, entryId) => {
  const response = await api
    .get(`/api/cms/events/${eventId}/materials/entries/${entryId}/decks.zip`, { responseType: 'blob' });
  downloadBlob(response.data, `materials-${entryId}.zip`, response.headers);
};

// A file on behalf of the speakers. `position` is required once the talk has files; the presenters
// are confirmed speaker spots (at least one).
export const addDeck = (eventId, entryId, file, { position, presenterAssignmentIds } = {}) => {
  const form = new FormData();
  form.append('file', file);
  if (position != null) form.append('position', position);
  (presenterAssignmentIds || []).forEach((id) => form.append('presenterAssignmentIds', id));
  return api.post(`/api/cms/events/${eventId}/materials/entries/${entryId}/decks`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};

// A new version of one file; the earlier ones stay.
export const addDeckVersion = (eventId, entryId, deckId, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/api/cms/events/${eventId}/materials/entries/${entryId}/decks/${deckId}/file`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};

// Removes the file with all its versions.
export const removeDeck = (eventId, entryId, deckId) => api
  .delete(`/api/cms/events/${eventId}/materials/entries/${entryId}/decks/${deckId}`).then((r) => r.data);

// The complete new order; a list that is not exactly the current set is a 409.
export const reorderDecks = (eventId, entryId, deckIds) => api
  .put(`/api/cms/events/${eventId}/materials/entries/${entryId}/decks/order`, { deckIds }).then((r) => r.data);

export const setDeckPresenters = (eventId, entryId, deckId, assignmentIds) => api
  .put(`/api/cms/events/${eventId}/materials/entries/${entryId}/decks/${deckId}/presenters`, { assignmentIds })
  .then((r) => r.data);

// "This is the order we present in": the ids in the order being confirmed.
export const confirmDeckOrder = (eventId, entryId, deckIds) => api
  .post(`/api/cms/events/${eventId}/materials/entries/${entryId}/order-confirmation`, { deckIds })
  .then((r) => r.data);

// The speaker's answer; setting it takes them off the files they present.
export const setPresentsWithoutSlides = (eventId, assignmentId, presentsWithoutSlides) => api
  .put(`/api/cms/events/${eventId}/materials/assignments/${assignmentId}/without-slides`, { presentsWithoutSlides })
  .then((r) => r.data);

// The admin has looked at the version.
export const reviewMaterial = (eventId, versionId) => api
  .put(`/api/cms/events/${eventId}/materials/${versionId}/review`).then((r) => r.data);

// Publish refuses with a 409 listing the deck's own `blockers`; unpublish takes it back off the
// public program.
export const publishMaterial = (eventId, deckId) => api
  .post(`/api/cms/events/${eventId}/materials/decks/${deckId}/publish`).then((r) => r.data);

export const unpublishMaterial = (eventId, deckId) => api
  .post(`/api/cms/events/${eventId}/materials/decks/${deckId}/unpublish`).then((r) => r.data);

// A person's suggested title for the talk: take it as the title in that language ('EN' | 'DE'), or
// dismiss it.
export const acceptTitleSuggestion = (eventId, entryId, language) => api
  .put(`/api/cms/events/${eventId}/materials/entries/${entryId}/title-suggestion/accept`, { language })
  .then((r) => r.data);

export const dismissTitleSuggestion = (eventId, entryId) => api
  .delete(`/api/cms/events/${eventId}/materials/entries/${entryId}/title-suggestion`).then((r) => r.data);
